/// <reference types="vite-plugin-svgr/client" />
import { LDEvaluationDetail, LDFlagSet, useFlags, withLDProvider } from 'launchdarkly-react-client-sdk';
import { ErrorBoundary } from 'react-error-boundary';
import { Auth0Provider } from '@brighte/passwordless-authenticator';
import { ErrorFallback } from '@components/ErrorFallback';
import { datadogRum } from '@datadog/browser-rum';
import { queryClient } from '@graphql';
import { FormPage } from '@pages/Form';
import { PackageProvider, PackageQuoteProvider } from '@provider';
import { SparkProvider } from '@spark-web/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { AUTH0_SCOPE, FeatureFlagKeys } from '@utils/constants';
import Maintenance from '../../form-components/src/Maintenance';

interface FeatureFlag extends LDFlagSet {
  [FeatureFlagKeys.CbaAcceptedQuotePackageScreen]: boolean;
  [FeatureFlagKeys.Maintenance]: { enableMaintenance: boolean; endDate?: string; endTime?: string };
  [FeatureFlagKeys.ProceedToApplicationForm]: boolean;
  [FeatureFlagKeys.UniversalLogin]: boolean;
}

export const App = () => {
  if (!import.meta.env.DEV) {
    datadogRum.init({
      applicationId: import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID,
      clientToken: import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'customer-connect',
      env: import.meta.env.VITE_ENVIRONMENT ?? 'development',
      version: import.meta.env.VITE_GIT_COMMIT ?? '1.0.0',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: true,
      proxy: import.meta.env.VITE_DATADOG_PROXY,
      enableExperimentalFeatures: ['feature_flags'],
      allowedTracingUrls: [import.meta.env.VITE_GRAPHQL_URL],
    });
    datadogRum.startSessionReplayRecording();
  }

  const flag = useFlags<FeatureFlag>();
  const { enableMaintenance, endTime, endDate } = flag[FeatureFlagKeys.Maintenance];

  return (
    <Auth0Provider
      userScope={AUTH0_SCOPE}
      domain={import.meta.env.VITE_AUTH_DOMAIN}
      clientId={import.meta.env.VITE_AUTH_CLIENT_ID}
      datadogInstance={datadogRum}
    >
      <QueryClientProvider client={queryClient()}>
        <SparkProvider supportedThemes={['brighte', 'pantheon']} theme="brighte">
          <PackageProvider>
            <PackageQuoteProvider>
              <Maintenance enabled={enableMaintenance} endTime={endTime} endDate={endDate}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <FormPage />
                </ErrorBoundary>
              </Maintenance>
            </PackageQuoteProvider>
          </PackageProvider>
        </SparkProvider>
      </QueryClientProvider>
    </Auth0Provider>
  );
};

const AppWithLD = withLDProvider({
  clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID ?? 'fake_id',
  options: {
    bootstrap: {
      [FeatureFlagKeys.CbaAcceptedQuotePackageScreen]: false,
      [FeatureFlagKeys.Maintenance]: {
        enableMaintenance: false,
        endDate: '',
        endTime: '',
      },
      [FeatureFlagKeys.ProceedToApplicationForm]: true,
      [FeatureFlagKeys.UniversalLogin]: false,
    },
    inspectors: [
      {
        type: 'flag-used',
        name: 'dd-inspector',
        method: (key: string, detail: LDEvaluationDetail) => {
          datadogRum.addFeatureFlagEvaluation(key, detail.value);
        },
      },
    ],
  },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  flags: {
    [FeatureFlagKeys.CbaAcceptedQuotePackageScreen]: false,
    [FeatureFlagKeys.Maintenance]: { enableMaintenance: false },
    [FeatureFlagKeys.ProceedToApplicationForm]: true,
    [FeatureFlagKeys.UniversalLogin]: false,
  },
})(App);

export default AppWithLD;
